import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import { X, Pencil, Check, RefreshCw, AlertTriangle, ShieldCheck, Plus, Trash2, FileWarning } from "lucide-react";
import { api, arabicError } from "../lib/api";

const LABELS = {
  invoice_number: "رقم الفاتورة", invoice_date: "تاريخ الفاتورة", invoice_time: "وقت الفاتورة", due_date: "تاريخ الاستحقاق",
  seller_name: "البائع", seller_shop: "محل البائع", seller_phone: "هاتف البائع", seller_location: "موقع البائع", seller_tax_id: "الرقم الضريبي للبائع",
  buyer_name: "المشتري", buyer_shop: "محل المشتري", buyer_phone: "هاتف المشتري", buyer_location: "موقع المشتري",
  currency: "العملة", subtotal: "المجموع الفرعي", discount: "الخصم", tax: "الضريبة", tax_rate: "نسبة الضريبة", grand_total: "الإجمالي النهائي",
  paid_amount: "المدفوع", remaining_amount: "المتبقي", payment_method: "طريقة الدفع", notes: "ملاحظات",
};
const ITEM_LABELS = { product: "المنتج", product_code: "الكود", quantity: "الكمية", unit: "الوحدة", price: "السعر", total: "الإجمالي" };
const ORDER = Object.keys(LABELS);
const CONF = { high: ["ثقة عالية", "bg-emerald-100 text-emerald-800"], medium: ["ثقة متوسطة", "bg-amber-100 text-amber-800"], low: ["ثقة منخفضة", "bg-red-100 text-red-800"] };
const RSTATUS = { needs_review: ["بحاجة لمراجعة", "bg-amber-100 text-amber-800"], auto_ok: ["متسقة تلقائياً", "bg-emerald-100 text-emerald-800"], confirmed: ["مؤكدة", "bg-[#EBF4F6] text-[#09637E]"] };

const fieldKeys = (d) => {
  const extras = Object.keys(d || {}).filter((k) => k !== "items" && !ORDER.includes(k));
  return [...ORDER, ...extras];
};
const itemKeys = (items) => {
  const ks = Object.keys(ITEM_LABELS);
  (items || []).forEach((it) => Object.keys(it || {}).forEach((k) => { if (!ks.includes(k)) ks.push(k); }));
  return ks;
};

export default function UploadedInvoiceReview({ view, onClose, onChanged, readOnly }) {
  const [edit, setEdit] = useState(false);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const d = view.ocr_data || {};
  const rv = view.ocr_review || null;
  const legacy = view.ocr_status === "done" && !rv;
  const uncertain = useMemo(() => new Set(rv?.uncertain_fields || []), [rv]);
  const issueFields = useMemo(() => new Set((rv?.issues || []).map((i) => i.field)), [rv]);
  const isPdf = view.mime_type === "application/pdf" || view.image_base64?.startsWith("data:application/pdf");
  const cur = edit ? draft : d;
  const keys = fieldKeys(cur);
  const ikeys = itemKeys(cur?.items);

  const startEdit = () => { setDraft(JSON.parse(JSON.stringify({ ...d, items: d.items || [] }))); setEdit(true); };
  const setF = (k, v) => setDraft((x) => ({ ...x, [k]: v }));
  const setI = (i, k, v) => setDraft((x) => { const items = [...x.items]; items[i] = { ...items[i], [k]: v }; return { ...x, items }; });
  const addItem = () => setDraft((x) => ({ ...x, items: [...x.items, {}] }));
  const rmItem = (i) => setDraft((x) => ({ ...x, items: x.items.filter((_, j) => j !== i) }));
  const [newKey, setNewKey] = useState("");
  const addField = () => { const k = newKey.trim().replace(/\s+/g, "_"); if (!k) return; setDraft((x) => ({ ...x, [k]: x[k] ?? "" })); setNewKey(""); };

  const confirm = async () => {
    setBusy(true);
    try {
      const r = await api.patch(`/uploaded-invoices/${view.id}/ocr-data`, { ocr_data: draft });
      toast.success(r.data.message);
      setEdit(false);
      onChanged({ ...view, ocr_data: r.data.ocr_data, ocr_review: r.data.ocr_review, review_status: "confirmed" });
    } catch (e) { toast.error(arabicError(e)); } finally { setBusy(false); }
  };
  const confirmAsIs = async () => { setDraft({ ...d, items: d.items || [] }); setBusy(true);
    try {
      const r = await api.patch(`/uploaded-invoices/${view.id}/ocr-data`, { ocr_data: { ...d, items: d.items || [] } });
      toast.success(r.data.message);
      onChanged({ ...view, ocr_data: r.data.ocr_data, ocr_review: r.data.ocr_review, review_status: "confirmed" });
    } catch (e) { toast.error(arabicError(e)); } finally { setBusy(false); }
  };
  const reread = async () => {
    if (!window.confirm("إعادة قراءة الفاتورة بالمحرك الدقيق؟ القراءة الحالية ستُحفظ في السجل ولن تُحذف، والملف الأصلي لا يتغير.")) return;
    setBusy(true);
    try { const r = await api.post(`/uploaded-invoices/${view.id}/reprocess`); toast.success(r.data.message); onChanged({ ...view, ocr_status: "pending", review_status: null }); }
    catch (e) { toast.error(arabicError(e)); } finally { setBusy(false); }
  };

  const hl = (k) => (issueFields.has(k) ? "border-red-400 bg-red-50" : uncertain.has(k) ? "border-amber-400 bg-amber-50" : "border-[#EBF4F6] bg-[#EBF4F6]");
  const ihl = (i, k) => (issueFields.has(`items[${i}].${k}`) ? "bg-red-50 text-red-700" : uncertain.has(`items[${i}].${k}`) ? "bg-amber-50 text-amber-800" : "");
  const inputCls = "w-full h-9 rounded-md border border-[#7AB2B2] px-2 text-sm bg-white";

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-2 md:p-4" onClick={onClose} dir="rtl">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="uploaded-view-modal">
        <div className="sticky top-0 bg-white border-b border-[#EBF4F6] p-3 md:p-4 flex items-center justify-between gap-2 flex-wrap z-10">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-extrabold text-[#333333]">تفاصيل الفاتورة المرفوعة</h3>
            {view.ocr_status === "done" && rv && <span className={`text-[11px] font-extrabold px-2 py-0.5 rounded-full ${CONF[rv.confidence]?.[1]}`} data-testid="ocr-confidence">{CONF[rv.confidence]?.[0]}</span>}
            {view.ocr_status === "done" && view.review_status && <span className={`text-[11px] font-extrabold px-2 py-0.5 rounded-full ${RSTATUS[view.review_status]?.[1]}`} data-testid="ocr-review-status">{RSTATUS[view.review_status]?.[0]}</span>}
            {legacy && <span className="text-[11px] font-extrabold px-2 py-0.5 rounded-full bg-gray-100 text-gray-700" data-testid="ocr-legacy">قراءة سابقة</span>}
            {rv?.passes === 2 && <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#EBF4F6] text-[#09637E]" title="أُجريت تمريرة مراجعة ثانية">مراجعة مزدوجة</span>}
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-lg hover:bg-[#EBF4F6] flex items-center justify-center" data-testid="close-uploaded-view"><X size={18} /></button>
        </div>

        <div className="p-3 md:p-4 space-y-3">
          {isPdf
            ? <iframe src={view.image_base64} title="فاتورة PDF" className="w-full h-[40vh] rounded-lg border border-[#EBF4F6]" data-testid="uploaded-pdf-preview" />
            : <img src={view.image_base64} alt="فاتورة" className="w-full max-h-[45vh] object-contain rounded-lg border border-[#EBF4F6] bg-[#F7FAFB]" />}

          {rv?.truncated && (
            <div className="flex items-start gap-2 bg-amber-50 border border-amber-300 rounded-lg p-3 text-sm text-amber-900" data-testid="ocr-truncated-warning">
              <FileWarning size={18} className="shrink-0 mt-0.5" />
              <span>المستند يحتوي {rv.page_count} صفحة — تمت قراءة أول {rv.pages_processed} صفحة فقط، والصفحات المتبقية لم تُدرج في الاستخراج. الملف الأصلي محفوظ كاملاً.</span>
            </div>
          )}
          {rv?.page_count > 1 && !rv.truncated && <div className="text-xs text-[#666666]">تمت قراءة {rv.pages_processed} صفحات من المستند.</div>}

          {view.ocr_status === "done" && rv && (rv.issues?.length > 0 || rv.uncertain_fields?.length > 0) && view.review_status !== "confirmed" && (
            <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 text-sm space-y-1" data-testid="ocr-issues">
              <div className="font-extrabold text-amber-900 flex items-center gap-1"><AlertTriangle size={16} /> تنبيهات المراجعة — لم يُصحَّح أي رقم تلقائياً</div>
              {(rv.issues || []).map((i, k) => <div key={k} className="text-amber-900" data-testid={`ocr-issue-${k}`}>• {i.message}</div>)}
              {rv.uncertain_fields?.length > 0 && <div className="text-amber-800">• حقول غير مؤكدة القراءة: {rv.uncertain_fields.map((f) => LABELS[f] || f).join("، ")}</div>}
              {rv.reviewer_note && <div className="text-amber-800">• {rv.reviewer_note}</div>}
            </div>
          )}
          {view.ocr_status === "done" && rv && rv.issues?.length === 0 && rv.uncertain_fields?.length === 0 && view.review_status !== "confirmed" && (
            <div className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-lg p-2 flex items-center gap-1" data-testid="ocr-consistent"><ShieldCheck size={16} /> الأرقام متسقة حسابياً (البنود، المجموع، الإجمالي).</div>
          )}
          {view.ocr_status === "done" && view.review_status === "confirmed" && rv?.issues?.length > 0 && (
            <div className="text-xs text-[#666666]">ملاحظة: بعد التأكيد ما تزال هناك {rv.issues.length} ملاحظة حسابية محفوظة كما هي (كما هو مطبوع في الفاتورة).</div>
          )}

          {view.ocr_status === "done" && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2" data-testid="ocr-fields">
                {keys.filter((k) => edit || (cur?.[k] !== null && cur?.[k] !== undefined && cur?.[k] !== "")).map((k) => (
                  <div key={k} className={`rounded-lg p-2 border ${hl(k)}`} data-testid={`ocr-field-${k}`}>
                    <div className="text-[11px] text-[#666666] font-bold flex items-center gap-1">{LABELS[k] || k}{uncertain.has(k) && <AlertTriangle size={11} className="text-amber-600" />}</div>
                    {edit ? <input className={inputCls} value={cur[k] ?? ""} onChange={(e) => setF(k, e.target.value)} data-testid={`ocr-input-${k}`} />
                      : <div className="font-bold text-[#333333] break-words" dir="auto">{String(cur[k])}</div>}
                  </div>
                ))}
              </div>
              {edit && (
                <div className="flex gap-2 items-center">
                  <input className={`${inputCls} max-w-xs`} placeholder="إضافة حقل جديد (مثال: رقم_الطلب)" value={newKey} onChange={(e) => setNewKey(e.target.value)} data-testid="ocr-new-field-key" />
                  <button onClick={addField} className="h-9 px-3 rounded-md border border-[#09637E] text-[#09637E] text-xs font-bold" data-testid="ocr-add-field">+ حقل</button>
                </div>
              )}

              {(edit || (cur?.items || []).length > 0) && (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs border border-[#7AB2B2] rounded-lg overflow-hidden min-w-[560px]" data-testid="ocr-items">
                    <thead><tr className="bg-[#09637E] text-white">
                      <th className="p-2 w-8">#</th>
                      {ikeys.map((h) => <th key={h} className="p-2 whitespace-nowrap">{ITEM_LABELS[h] || h}</th>)}
                      {edit && <th className="p-2 w-8" />}
                    </tr></thead>
                    <tbody>
                      {(cur.items || []).map((it, i) => (
                        <tr key={i} className="border-t border-[#EBF4F6] text-center" data-testid={`ocr-item-${i}`}>
                          <td className="p-1 text-[#666666]">{i + 1}</td>
                          {ikeys.map((k) => (
                            <td key={k} className={`p-1 ${ihl(i, k)}`}>
                              {edit ? <input className="w-full min-w-[70px] h-8 rounded border border-[#7AB2B2] px-1 text-xs bg-white" value={it?.[k] ?? ""} onChange={(e) => setI(i, k, e.target.value)} data-testid={`ocr-item-input-${i}-${k}`} />
                                : <span dir="auto">{it?.[k] ?? ""}</span>}
                            </td>
                          ))}
                          {edit && <td className="p-1"><button onClick={() => rmItem(i)} className="text-red-500" data-testid={`ocr-item-remove-${i}`}><Trash2 size={14} /></button></td>}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {edit && <button onClick={addItem} className="mt-2 h-8 px-3 rounded-md border border-[#09637E] text-[#09637E] text-xs font-bold flex items-center gap-1" data-testid="ocr-add-item"><Plus size={12} /> إضافة بند</button>}
                </div>
              )}

              {!readOnly && (
                <div className="flex flex-wrap gap-2 pt-1 border-t border-[#EBF4F6]">
                  {edit ? (
                    <>
                      <button onClick={confirm} disabled={busy} className="h-10 px-4 rounded-lg bg-[#09637E] text-white text-sm font-bold flex items-center gap-1 disabled:opacity-60" data-testid="ocr-confirm-btn"><Check size={16} /> تأكيد البيانات</button>
                      <button onClick={() => setEdit(false)} disabled={busy} className="h-10 px-4 rounded-lg border border-[#7AB2B2] text-sm font-bold" data-testid="ocr-cancel-edit">إلغاء</button>
                    </>
                  ) : (
                    <>
                      <button onClick={startEdit} className="h-10 px-4 rounded-lg border-2 border-[#09637E] text-[#09637E] text-sm font-bold flex items-center gap-1" data-testid="ocr-edit-btn"><Pencil size={15} /> تعديل</button>
                      {view.review_status !== "confirmed" && <button onClick={confirmAsIs} disabled={busy} className="h-10 px-4 rounded-lg bg-[#09637E] text-white text-sm font-bold flex items-center gap-1 disabled:opacity-60" data-testid="ocr-confirm-asis-btn"><Check size={16} /> تأكيد كما هي</button>}
                      <button onClick={reread} disabled={busy} className="h-10 px-4 rounded-lg border border-[#7AB2B2] text-[#333333] text-sm font-bold flex items-center gap-1 disabled:opacity-60" data-testid="ocr-reread-btn"><RefreshCw size={15} /> إعادة القراءة</button>
                    </>
                  )}
                </div>
              )}
              {view.ocr_history?.length > 0 && <div className="text-[11px] text-[#666666]">القراءات السابقة المحفوظة: {view.ocr_history.length}</div>}
            </div>
          )}
          {view.ocr_status === "failed" && (
            <div className="space-y-2">
              <div className="text-sm text-red-500 font-bold" data-testid="ocr-failed">تعذر استخراج البيانات تلقائياً — الملف محفوظ ويمكنك الاطلاع عليه</div>
              {!readOnly && <button onClick={reread} disabled={busy} className="h-10 px-4 rounded-lg border border-[#7AB2B2] text-sm font-bold flex items-center gap-1" data-testid="ocr-reread-btn"><RefreshCw size={15} /> إعادة القراءة</button>}
            </div>
          )}
          {view.ocr_status === "pending" && <div className="text-sm text-amber-600 font-bold" data-testid="ocr-pending">جارٍ استخراج البيانات بدقة (قد تستغرق حتى دقيقة)... حدّث القائمة بعد قليل</div>}
        </div>
      </div>
    </div>
  );
}
